import React, { useState, useEffect, useRef, useCallback } from 'react'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { APIURL, TZ } from '../../../Global'
import InvoiceModal from './InvoiceModal'
import './Liabilities.css'

export default function Liabilities({ hospitalId }) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [bills, setBills] = useState([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const perPage = 10

  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [selectedBill, setSelectedBill] = useState(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0) // Add refresh trigger

  const observer = useRef()
  const lastBillElementRef = useCallback(
    (node) => {
      if (loadingMore) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1)
        }
      })
      if (node) observer.current.observe(node)
    },
    [loadingMore, hasMore]
  )

  const API = `${APIURL}/api/v1/staff/vendor-liability/`

  // Function to get currency symbol
  const getCurrencySymbol = (currency) => {
    if (!currency) return '$';

    const currencyLower = currency.toLowerCase();
    switch (currencyLower) {
      case 'crc':
      case 'col':
      case 'colon':
        return '₡';
      case 'usd':
      case 'dollar':
        return '$';
      default:
        return '$';
    }
  }

  // Function to format amount with currency
  const formatAmount = (amount, currency) => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol} ${amount || 0}/-`;
  }

  useEffect(() => {
    if (!hospitalId) return
    fetchBills(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalId, refreshTrigger]) // Add refreshTrigger to dependencies

  useEffect(() => {
    if (page > 1) {
      fetchBills(page, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const fetchBills = async (pageNumber, isInitial = false) => {
    if (isInitial) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }
    setError(null)

    try {
      const tokenString = sessionStorage.getItem('usertoken') || ''
      const v2 = tokenString.replace(/["']+/g, '')

      const params = new URLSearchParams({
        hospital_id: hospitalId,
        per_page: perPage,
        page: pageNumber,
        timeZone: TZ || 'Asia/Calcutta',
      })

      const url = `${API}?${params.toString()}`
      const res = await axios.get(url, {
        headers: { Authorization: 'Token ' + v2 },
      })

      const responseData = res.data?.message || []
      const normalized = Array.isArray(responseData) ? responseData : []
      const hasNext = res.data?.has_next || false
      if (responseData.length === 0) {
        setError('liability.noLiabilities')
      }
      if (isInitial) {
        setBills(normalized)
      } else {
        setBills((prev) => [...prev, ...normalized])
      }

      setHasMore(hasNext)
    } catch (err) {
      console.error('Liabilities fetch error:', err)
      setError('liability.failedToLoad')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const openInvoice = (bill) => {
    setSelectedBill(bill)
    setShowInvoiceModal(true)
  }

  const closeInvoice = () => {
    setShowInvoiceModal(false)
    setSelectedBill(null)
  }

  // Function to trigger refresh after successful payment
  const handlePaymentSuccess = () => {
    setRefreshTrigger(prev => prev + 1) // Trigger refetch
    closeInvoice()
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2) 
    return `${day}-${month}-${year}`
  }

  if (error) {
    return (
      <div className="error-state">
        <i className="fa fa-exclamation-circle"></i>
        <p>{t(error)}</p>
      </div>
    );
  }

  return (
    <div className="liabilities-container">
      <div className="liabilities-header">
        <h4>{t('liability.title') || 'Liabilities'}</h4>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('liability.loading') || 'Loading...'}</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <i className="fa fa-exclamation-circle"></i>
          <p>{error}</p>
        </div>
      ) : bills.length === 0 ? (
        <div className="empty-state">
          <i className="fa fa-inbox"></i>
          <p>{t('liability.noLiabilities') || 'No liabilities found.'}</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="liabilities-table">
            <thead>
              <tr>
                <th>{t('liability.table.id') || 'ID'}</th>
                <th>{t('liability.table.item') || 'Item'}</th>
                <th>{t('liability.table.request') || 'Request'}</th>
                <th>{t('liability.table.paidAmount') || 'Paid amount'}</th>
                <th>{t('liability.table.totalAmount') || 'Total amount'}</th>
                <th>{t('liability.table.liability') || 'Liability'}</th>
                <th className="action-col"></th>
              </tr>
            </thead>
            <tbody>
              {bills.map((bill, index) => {
                const isLast = index === bills.length - 1
                const currency = bill.currency;

                return (
                  <tr key={bill.id} ref={isLast ? lastBillElementRef : null}>
                    <td className="id-cell">{bill.id}</td>
                    <td>{bill.item_name || '-'}</td>
                    <td>{formatDate(bill.bill_generated_date)}</td>
                    <td>{formatAmount(bill.paid_amount, currency)}</td>
                    <td>{formatAmount(bill.total_license_amount, currency)}</td>
                    <td className="liability-cell">{formatAmount(bill.liability, currency)}</td>
                    <td>
                      <button
                        className="invoice-btn"
                        onClick={() => openInvoice(bill)}
                      >
                        {t('liability.invoiceButton') || 'Invoice'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {loadingMore && (
            <div className="loading-more">
              <div className="spinner-small"></div>
              <span>{t('liability.loadingMore') || 'Loading more...'}</span>
            </div>
          )}
        </div>
      )}

      <InvoiceModal
        show={showInvoiceModal}
        onHide={closeInvoice}
        onPaymentSuccess={handlePaymentSuccess} // Pass success callback
        billId={selectedBill?.id}
        invoiceData={selectedBill}
        hospitalId={hospitalId}
      />
    </div>
  )
}